import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { TypedMemo } from '../../common/types';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { useFollowTrack } from '../hooks/useFollowTrack';
import { TracksTable, TRACKS_EXPORT_FILENAME } from '../components/TracksTable/TracksTable';
import { ExportCsvButton } from '../components/ExportCsvButton/ExportCsvButton';
import { MeiliTrackData, TrackSearchField } from '../components/SearchField';
import { WatchlistSection } from './WatchlistSection';
import { WatchlistPagination } from './WatchlistPagination';
import { WATCHLIST_PAGE_SIZE } from './watchlistPageSize';

interface WatchlistTracksPanelProps {
  selectedDate: Date;
  selectedDateValue: string;
}

/** The followed tracks, with search-to-follow and CSV export. */
const WatchlistTracksPanelComponent: React.FC<WatchlistTracksPanelProps> = ({ selectedDate, selectedDateValue }) => {
  const intl = useIntl();
  const trpcMusicAPI = useTRPCAPI();
  const { showSuccess, showError } = useShowToast();
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useTRPCQuery(
    trpcMusicAPI.tracks.listFollowed.queryOptions({
      offset: (page - 1) * WATCHLIST_PAGE_SIZE,
      limit: WATCHLIST_PAGE_SIZE,
    })
  );

  const { followTrack, isPending } = useFollowTrack({
    onSuccess: () => {
      showSuccess(
        intl.formatMessage({ id: 'app.sml.tracks.follow.success', defaultMessage: 'Track added to your followed list' })
      );
    },
    onError: (followError) => {
      showError(
        followError.message ||
          intl.formatMessage({ id: 'app.sml.tracks.follow.error', defaultMessage: 'Failed to follow track' })
      );
    },
  });

  const onSelected = useCallback((track: MeiliTrackData) => followTrack(track.id), [followTrack]);
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return (
    <WatchlistSection
      icon={<FontAwesomeIcon icon={faMusic} />}
      title={intl.formatMessage({ id: 'app.sml.home.tab.tracks', defaultMessage: 'Tracks' })}
      exportButton={
        <ExportCsvButton kind="tracks" filename={TRACKS_EXPORT_FILENAME} isEmpty={(data?.total ?? 0) === 0} />
      }
      search={
        <Row className="mb-3">
          <Col xs={12} lg={8}>
            <TrackSearchField
              placeholder={intl.formatMessage({
                id: 'app.search.tracks.placeholder',
                defaultMessage: 'Search for tracks...',
              })}
              onTrackSelected={onSelected}
              isDisabled={isPending}
            />
          </Col>
        </Row>
      }
      error={error}
      retry={retry}
    >
      <TracksTable
        tracks={data?.tracks ?? []}
        isLoading={isLoading}
        selectedDateValue={selectedDateValue}
        selectedDate={selectedDate}
      />
      <WatchlistPagination
        page={page}
        onPageChange={setPage}
        hasNext={data?.hasMore === true}
        total={data?.total ?? 0}
        entityLabel={intl.formatMessage({ id: 'app.sml.home.entity.tracks', defaultMessage: 'tracks' })}
      />
    </WatchlistSection>
  );
};

export const WatchlistTracksPanel = TypedMemo(WatchlistTracksPanelComponent);
